import { z } from 'zod';

import { defineCommand } from '@nesso/common-contracts/define';
import { practiceIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import {
    billableActionSchema,
    cancellationFeedbackSchema,
    cardSchema,
} from './subscription.models';

export const SUBSCRIPTION_CANCEL_PLAN_TYPE = 'subscription/cancel-plan';
export const SUBSCRIPTION_CREATE_CHECKOUT_SESSION_TYPE = 'subscription/create-checkout-session';
export const SUBSCRIPTION_UPDATE_PLAN_TYPE = 'subscription/update-plan';

export const subscriptionCancelPlan = defineCommand(
    SUBSCRIPTION_CANCEL_PLAN_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        feedback: cancellationFeedbackSchema.meta(metadata.nonSensitive),
        comment: z.string().trim().min(1).max(500).optional(),
    }),
);

export const subscriptionCreateCheckoutSession = defineCommand(
    SUBSCRIPTION_CREATE_CHECKOUT_SESSION_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        planCode: z.string().min(1).meta(metadata.nonSensitive),
        successUrl: z.url(),
        cancelUrl: z.url(),
    }),
    z.object({ url: z.url() }),
);

export const subscriptionUpdatePlan = defineCommand(
    SUBSCRIPTION_UPDATE_PLAN_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        planCode: z.string().min(1).meta(metadata.nonSensitive),
    }),
    z.object({ scheduleId: z.string().min(1).nullable().meta(metadata.nonSensitive) }),
);

// scheduler jobs: the {type, payload} envelope IS the scheduler input
export const EXPIRE_TRIALS_JOB_TYPE = 'subscription/expire-trials';
export const SEND_TRIAL_ENDING_NOTIFICATIONS_JOB_TYPE =
    'subscription/send-trial-ending-notifications';
export const SEND_TRIAL_EXPIRED_NOTIFICATIONS_JOB_TYPE =
    'subscription/send-trial-expired-notifications';
export const SEND_ACCOUNT_CLOSING_NOTIFICATIONS_JOB_TYPE =
    'subscription/send-trial-account-closing-notifications';

export const expireTrialsJob = defineCommand(EXPIRE_TRIALS_JOB_TYPE, z.object({}));
export const sendTrialEndingNotificationsJob = defineCommand(
    SEND_TRIAL_ENDING_NOTIFICATIONS_JOB_TYPE,
    z.object({}),
);
export const sendTrialExpiredNotificationsJob = defineCommand(
    SEND_TRIAL_EXPIRED_NOTIFICATIONS_JOB_TYPE,
    z.object({}),
);
export const sendAccountClosingNotificationsJob = defineCommand(
    SEND_ACCOUNT_CLOSING_NOTIFICATIONS_JOB_TYPE,
    z.object({}),
);

export const SUBSCRIPTION_CHECKOUT_COMPLETED_TYPE = 'subscription/checkout-completed';
export const SUBSCRIPTION_UPDATED_TYPE = 'subscription/updated';
export const SUBSCRIPTION_SCHEDULE_CHANGED_TYPE = 'subscription/schedule-changed';
export const SUBSCRIPTION_PAYMENT_METHOD_CHANGED_TYPE = 'subscription/payment-method-changed';
export const SUBSCRIPTION_ACCOUNT_CREDIT_CHANGED_TYPE = 'subscription/account-credit-changed';
export const SUBSCRIPTION_CANCELLATION_REQUESTED_TYPE = 'subscription/cancellation-requested';
export const SUBSCRIPTION_EXPIRE_TRIAL_TYPE = 'subscription/expire-trial';
export const SUBSCRIPTION_PAYMENT_FAILED_TYPE = 'subscription/payment-failed';
export const SUBSCRIPTION_PAYMENT_SUCCEEDED_TYPE = 'subscription/payment-succeeded';

export const subscriptionCheckoutCompleted = defineCommand(
    SUBSCRIPTION_CHECKOUT_COMPLETED_TYPE,
    z.object({
        practiceId: z.uuid(),
        planCode: z.string().min(1),
        externalCustomerId: z.string().min(1),
        externalSubscriptionId: z.string().min(1),
        externalCheckoutSessionId: z.string().min(1),
        currentPeriodStart: z.iso.datetime().or(z.date()),
        currentPeriodEnd: z.iso.datetime().or(z.date()),
        card: cardSchema,
        nextInvoiceAmountCents: z.number().int().nullable(),
        nextInvoiceAt: z.iso.datetime().or(z.date()).nullable(),
        externalEventId: z.string().min(1),
    }),
);

export const subscriptionUpdated = defineCommand(
    SUBSCRIPTION_UPDATED_TYPE,
    z.object({
        externalSubscriptionId: z.string().min(1),
        planCode: z.string().min(1),
        currentPeriodStart: z.iso.datetime().or(z.date()),
        currentPeriodEnd: z.iso.datetime().or(z.date()),
        cancelAt: z.number().int().nullable(),
        card: cardSchema.nullable(),
        nextInvoiceAmountCents: z.number().int().nullable(),
        nextInvoiceAt: z.iso.datetime().or(z.date()).nullable(),
        externalEventId: z.string().min(1),
    }),
);

export const subscriptionScheduleChanged = defineCommand(
    SUBSCRIPTION_SCHEDULE_CHANGED_TYPE,
    z.object({
        externalSubscriptionId: z.string().min(1),
        nextPlanCode: z.string().min(1),
        nextInvoiceAmountCents: z.number().int().nullable(),
        nextInvoiceAt: z.iso.datetime().or(z.date()).nullable(),
        externalEventId: z.string().min(1),
    }),
);

export const subscriptionPaymentMethodChanged = defineCommand(
    SUBSCRIPTION_PAYMENT_METHOD_CHANGED_TYPE,
    z.object({
        externalCustomerId: z.string().min(1).meta(metadata.nonSensitive),
        card: cardSchema,
        externalEventId: z.string().min(1).meta(metadata.nonSensitive),
    }),
);

export const subscriptionAccountCreditChanged = defineCommand(
    SUBSCRIPTION_ACCOUNT_CREDIT_CHANGED_TYPE,
    z.object({
        externalCustomerId: z.string().min(1).meta(metadata.nonSensitive),
        accountCreditCents: z.number().int().nonnegative().meta(metadata.nonSensitive),
        // Absent for a future job-initiated refresh, which has no Stripe event behind it.
        externalEventId: z.string().min(1).optional().meta(metadata.nonSensitive),
    }),
);

export const subscriptionCancellationRequested = defineCommand(
    SUBSCRIPTION_CANCELLATION_REQUESTED_TYPE,
    z.object({
        externalSubscriptionId: z.string().min(1),
        // Nullable: messages published before this field existed carry no timestamp, and the
        // provider leaves it unset on a subscription that ends without an explicit cancellation.
        canceledAt: z.iso.datetime().or(z.date()).nullable().optional(),
        externalEventId: z.string().min(1),
    }),
);

export const subscriptionExpireTrial = defineCommand(
    SUBSCRIPTION_EXPIRE_TRIAL_TYPE,
    z.object({
        subscriptionId: z.uuid(),
    }),
);

export const subscriptionPaymentFailed = defineCommand(
    SUBSCRIPTION_PAYMENT_FAILED_TYPE,
    z.object({
        externalSubscriptionId: z.string().min(1),
        nextRetryAt: z.iso.datetime().or(z.date()).nullable(),
        externalEventId: z.string().min(1),
    }),
);

export const subscriptionPaymentSucceeded = defineCommand(
    SUBSCRIPTION_PAYMENT_SUCCEEDED_TYPE,
    z.object({
        externalSubscriptionId: z.string().min(1),
        externalEventId: z.string().min(1),
    }),
);

export const SUBSCRIPTION_CREATE_PORTAL_SESSION_TYPE = 'subscription/create-portal-session';
export const SUBSCRIPTION_REACTIVATE_TYPE = 'subscription/reactivate';

export const subscriptionCreatePortalSession = defineCommand(
    SUBSCRIPTION_CREATE_PORTAL_SESSION_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        returnUrl: z.url(),
        target: z.enum(['manage-plan', 'payment-method']).optional().meta(metadata.nonSensitive),
    }),
    z.object({ url: z.url() }),
);

export const subscriptionReactivate = defineCommand(
    SUBSCRIPTION_REACTIVATE_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
);

export const SUBSCRIPTION_START_TRIAL_TYPE = 'subscription/start-trial';

export const subscriptionStartTrial = defineCommand(
    SUBSCRIPTION_START_TRIAL_TYPE,
    z.object({
        practiceId: z.uuid().meta(metadata.nonSensitive),
        practiceMemberId: z.uuid().meta(metadata.nonSensitive),
    }),
);

export const SUBSCRIPTION_RECORD_USAGE_TYPE = 'subscription/record-usage';

export const subscriptionRecordUsage = defineCommand(
    SUBSCRIPTION_RECORD_USAGE_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: z.uuid().meta(metadata.nonSensitive),
        // opaque to subscription — the caller's id for the thing it did, used to deduplicate
        recordId: z.uuid().meta(metadata.recordId),
        action: billableActionSchema.meta(metadata.nonSensitive),
    }),
);

export const SUBSCRIPTION_GRANT_PERIOD_CREDIT_TYPE = 'subscription/grant-period-credit';

export const subscriptionGrantPeriodCredit = defineCommand(
    SUBSCRIPTION_GRANT_PERIOD_CREDIT_TYPE,
    z.object({
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        creditKey: z.string().min(1).max(200).meta(metadata.recordId),
        description: z.string().min(1).max(500).meta(metadata.nonSensitive),
        // Opaque passthrough, echoed back verbatim on `subscription/credit-granted`. Nothing in
        // this context may read or branch on its contents — that is what keeps a caller's
        // vocabulary out of subscription.
        metadata: z.record(z.string(), z.string()),
    }),
);

export const SUBSCRIPTION_DELETE_TYPE = 'subscription/delete';

export const subscriptionDelete = defineCommand(
    SUBSCRIPTION_DELETE_TYPE,
    z.object({
        practiceId: z.uuid().meta(metadata.nonSensitive),
    }),
);
